import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { MapPin, Gift, Store, ShieldCheck, Loader2, CheckCircle2, BookOpen, ArrowRight } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { containsProfanity, isReservedUsername, usernameHasProfanity } from '@/lib/profanity';

const bullets = (text) => (text || '').split('\n').map(s => s.trim()).filter(Boolean);

export default function WelcomeDialog({ content = {}, onDone }) {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const formatOk = /^[A-Za-z0-9_-]{6,24}$/.test(username);
  const isProfane = usernameHasProfanity(username);
  const isReserved = isReservedUsername(username);
  const valid = formatOk && !isProfane && !isReserved;
  const hint = isProfane ? t('welcome.profanityHint')
    : isReserved ? t('welcome.reservedHint')
    : t('welcome.usernameHint');

  const submit = async () => {
    if (!agreed) { toast.error(t('welcome.agreeError')); return; }
    if (!valid) { toast.error(t('welcome.usernameError')); return; }
    setSubmitting(true);
    try {
      const res = await api.post('/me/onboard', { username: username.trim(), agreed });
      toast.success(t('welcome.welcomeAboard', { username: res.data.username }));
      onDone?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('welcome.setupError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-brand-ink/60 backdrop-blur-sm" data-testid="welcome-dialog">
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', damping: 26, stiffness: 240 }}
        className="w-full max-w-lg max-h-[92vh] overflow-y-auto bg-brand-paper rounded-3xl shadow-2xl"
      >
        <div className="bg-brand text-white px-7 py-6 rounded-t-3xl">
          <div className="flex items-center gap-2 text-white/80 text-xs uppercase tracking-[0.25em] mb-2">
            <MapPin className="w-4 h-4" /> CurbAlert
          </div>
          <h2 className="font-display font-black text-3xl leading-tight" data-testid="welcome-title">{content.welcome_screen_title || t('welcome.titleDefault')}</h2>
        </div>

        <div className="px-7 py-6 space-y-6">
          <p className="text-brand-ink/80 text-sm leading-relaxed" data-testid="welcome-intro">{content.welcome_screen_intro}</p>

          <a
            href="/tutorial"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="welcome-tutorial-link"
            className="flex items-center justify-between gap-3 rounded-2xl bg-brand/5 hover:bg-brand/10 transition px-4 py-3 group"
          >
            <span className="flex items-center gap-2.5 text-sm font-medium text-brand-ink">
              <BookOpen className="w-4 h-4 text-brand" /> New here? See how CurbAlert works
            </span>
            <ArrowRight className="w-4 h-4 text-brand group-hover:translate-x-0.5 transition-transform" />
          </a>

          {bullets(content.welcome_screen_features).length > 0 && (
            <div>
              <h3 className="font-display font-bold text-brand-ink mb-2 flex items-center gap-2"><Gift className="w-4 h-4 text-[#7A9A78]" /> {t('welcome.whatYouCanDo')}</h3>
              <ul className="space-y-1.5">
                {bullets(content.welcome_screen_features).map((f, i) => (
                  <li key={`feat-${i}-${f.slice(0, 12)}`} className="flex items-start gap-2 text-sm text-brand-slate">
                    <CheckCircle2 className="w-4 h-4 text-[#7A9A78] mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {bullets(content.welcome_screen_rules).length > 0 && (
            <div>
              <h3 className="font-display font-bold text-brand-ink mb-2 flex items-center gap-2"><Store className="w-4 h-4 text-[#C86F53]" /> {t('welcome.communityRules')}</h3>
              <ul className="space-y-1.5">
                {bullets(content.welcome_screen_rules).map((r, i) => (
                  <li key={`rule-${i}-${r.slice(0, 12)}`} className="flex items-start gap-2 text-sm text-brand-slate">
                    <span className="text-[#C86F53] mt-0.5">•</span> {r}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="pt-2 border-t border-black/10 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-brand-dim">{t('welcome.chooseUsername')}</label>
              <Input
                data-testid="welcome-username-input"
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder={t('welcome.usernamePlaceholder')}
                className="mt-2 rounded-xl bg-black/5 border-transparent focus:bg-white focus:border-brand"
              />
              <p className={`mt-1.5 text-xs ${username && !valid ? 'text-[#C86F53]' : 'text-brand-dim'}`}>
                {hint}
              </p>
              <p data-testid="welcome-no-real-name" className="mt-1.5 text-xs font-medium text-[#C86F53]">
                {t('welcome.noRealName')}
              </p>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox data-testid="welcome-agree-checkbox" checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5" />
              <span className="text-sm text-brand-ink">{content.welcome_screen_agree_label || t('welcome.agreeLabel')}</span>
            </label>

            <Button
              data-testid="welcome-submit-btn"
              onClick={submit}
              disabled={submitting || !agreed || !valid}
              className="w-full rounded-full bg-brand hover:bg-brand-hover text-white py-6 text-base"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <ShieldCheck className="w-5 h-5 mr-2" />}
              {t('welcome.agreeStart')}
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
